/**
 * Remembers that this browser's user is in the `b2b devs` cohort, so dev-mode ELIGIBILITY survives
 * impersonation (B2B-2345) the way the switch already does.
 *
 * Why a marker at all: an impersonated session identifies in PostHog as the customer (B2B-2158), so
 * `dev-mode` resolves against a person who is not in the cohort and comes back false. Nothing
 * PostHog can key a flag on survives the identity switch, and keying on `isImpersonated` instead
 * would hand dev mode to every super admin rather than to the cohort.
 *
 * Written and cleared only from a NON-impersonated session, and only once the flag has actually
 * resolved. The clear is what holds the audience: a user the cohort no longer includes wipes the
 * marker on their own next session, and impersonation can never start cold — `changeUser` needs the
 * admin's tokens already present — so that clear always runs before they could impersonate.
 *
 * Not a security boundary (handbook §18 §5). If PostHog never resolves — ad blocker, offline, a
 * build with no `VITE_POSTHOG_TOKEN` — the marker cannot be cleared and a stale one survives; the
 * button only copies values already in that browser.
 */
export const DEV_MODE_ELIGIBILITY_STORAGE_KEY = 'xceed-dev-mode-eligible';

// Guarded like the switch: a WebView with storage disabled throws on access rather than returning
// null, and a debugging convenience is never worth breaking a launch over.
export const readDevModeEligibility = (): boolean => {
  try {
    return globalThis.localStorage.getItem(DEV_MODE_ELIGIBILITY_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

// Removes rather than writing 'false': there is no reason to leave a key behind in the browser of
// someone who is not eligible, and "absent" already reads back as not eligible.
export const persistDevModeEligibility = (isEligible: boolean): void => {
  try {
    if (isEligible) globalThis.localStorage.setItem(DEV_MODE_ELIGIBILITY_STORAGE_KEY, 'true');
    else globalThis.localStorage.removeItem(DEV_MODE_ELIGIBILITY_STORAGE_KEY);
  } catch {
    // Not persisted; the in-memory state still applies for this session.
  }
};
